import { ImgHTMLAttributes } from "react";
import {
  gallery600WebpToAvif,
  webpToAvif,
  webpToGallery600,
} from "@/lib/image-map";

interface PictureImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string;
  smallSrc?: string;
  mediaQuery?: string;
  sameImageVariants?: boolean;
  disableSmallVariant?: boolean;
  largeVariantWidth?: number;
}

/**
 * Renders a <picture> element that serves AVIF when available, with
 * WebP fallback. Optionally supports a smaller variant gated by media query
 * (typically used for mobile-vs-desktop hero images). When no alternate crop
 * is provided, matching 600px and 1200px variants are selected by rendered
 * width so narrow desktop cards do not download oversized images.
 *
 * `src` should be the resolved WebP URL (from image-map). The corresponding
 * AVIF URL is looked up in `webpToAvif`. If no AVIF exists, the WebP is used
 * as the only source.
 *
 * Always pass `width` and `height` (intrinsic image dimensions) to prevent CLS.
 */
export function PictureImage({
  src,
  smallSrc,
  mediaQuery = "(max-width: 768px)",
  sameImageVariants = false,
  disableSmallVariant = false,
  largeVariantWidth = 1200,
  alt = "",
  sizes: sizesProp,
  ...imgProps
}: PictureImageProps) {
  const largeAvif = webpToAvif[src];
  const resolvedSmallSrc = disableSmallVariant
    ? undefined
    : smallSrc
      ? (webpToGallery600[smallSrc] ?? smallSrc)
      : webpToGallery600[src];
  const smallAvif = resolvedSmallSrc
    ? (gallery600WebpToAvif[resolvedSmallSrc] ?? webpToAvif[resolvedSmallSrc])
    : undefined;
  const sizes = sizesProp ?? "100vw";
  const useWidthVariants =
    Boolean(sizesProp) && (!smallSrc || sameImageVariants);
  const widthAvifSrcSet =
    useWidthVariants && smallAvif && largeAvif
      ? `${smallAvif} 600w, ${largeAvif} ${largeVariantWidth}w`
      : undefined;
  const widthWebpSrcSet =
    useWidthVariants && resolvedSmallSrc
      ? `${resolvedSmallSrc} 600w, ${src} ${largeVariantWidth}w`
      : undefined;

  return (
    <picture>
      {widthAvifSrcSet && (
        <source type="image/avif" srcSet={widthAvifSrcSet} sizes={sizes} />
      )}
      {widthWebpSrcSet && (
        <source srcSet={widthWebpSrcSet} sizes={sizes} />
      )}
      {!useWidthVariants && smallAvif && (
        <source type="image/avif" media={mediaQuery} srcSet={smallAvif} />
      )}
      {!useWidthVariants && resolvedSmallSrc && (
        <source media={mediaQuery} srcSet={resolvedSmallSrc} />
      )}
      {largeAvif && !widthAvifSrcSet && (
        <source type="image/avif" srcSet={largeAvif} />
      )}
      <img src={src} alt={alt} sizes={sizes} {...imgProps} />
    </picture>
  );
}
